import { FC, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { PayrollConceptFragment } from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { ConceptSelector } from 'modules/finance/components/FinancialWorkspace/components/ConceptSelector'

import { useFinancialWorkspaceSettings } from './FinancialWorkspaceSettingsProvider'
import { FinancialWorkspaceSettingsFormValues } from './constants'

interface ConceptsProps {
  title: string
  mandatory?: boolean
  defaultConceptIds?: number[]
  field: keyof FinancialWorkspaceSettingsFormValues
  setExpensesConceptMissing?: (missing: boolean) => void
  setFieldValue: (field: keyof FinancialWorkspaceSettingsFormValues, value: number[]) => void
  description?: string
}

export const Concepts: FC<ConceptsProps> = ({
  title,
  field,
  description,
  setFieldValue,
  mandatory = false,
  defaultConceptIds,
  setExpensesConceptMissing,
}) => {
  const { isResetting, setIsResetting, companyCostConcept, expensesConcept, errors } =
    useFinancialWorkspaceSettings()
  const [selectedConcepts, setSelectedConcepts] = useState<PayrollConceptFragment[] | undefined>(
    undefined
  )

  const onValueChange = (concepts: PayrollConceptFragment[]) => {
    setFieldValue(
      field,
      concepts.map((o) => o.id)
    )
    setSelectedConcepts(concepts)
    setIsResetting(false)
  }

  useUnsafeEffect(() => {
    if (isResetting) {
      if (field === 'conceptIdsToAdd' && companyCostConcept) {
        setSelectedConcepts([companyCostConcept])
      } else if (field === 'conceptIdsToSubtract' && expensesConcept) {
        setSelectedConcepts([expensesConcept])
      }
    }
  }, [isResetting, field, companyCostConcept, expensesConcept])

  useUnsafeEffect(() => {
    const expensesConceptDetected = selectedConcepts?.some((o) => o.name === 'expenses')
    const expensesConceptMissing = !expensesConceptDetected
    if (expensesConceptMissing) {
      setExpensesConceptMissing?.(true)
    } else {
      setExpensesConceptMissing?.(false)
    }
  }, [selectedConcepts, setExpensesConceptMissing])
  const fieldError = errors?.[field]
  const error = Array.isArray(fieldError) ? fieldError[0] : fieldError

  return (
    <Box width="full" gap="s12">
      <Box gap="s2">
        <Text.Normal weight="medium" size="300">
          {title}
        </Text.Normal>
        {description && <Text.Normal size="200">{description}</Text.Normal>}
      </Box>
      <ConceptSelector
        label={title}
        filter={false}
        error={error}
        alwaysOpen={false}
        mandatory={mandatory}
        onValueChange={onValueChange}
        defaultConceptIds={defaultConceptIds}
        selectedConcepts={selectedConcepts}
        emptyLabel={i18n.t('finance.financial_workspace.settings.labor_cost.select_concepts')}
      />
    </Box>
  )
}
